/**
 * 购物车 BOGO 展示控制器：供抽屉和 /cart 页面共用。
 * 入参：无；通过 DOM、购物车事件和 KMCartBogoRules 获取配置及资格信息。
 * 出参：无；向 window.KMCartBogo 暴露初始化、活动查询和加购资格校验接口。
 * 关键流程：读取配置 → 接收商品上下文 → 渲染推荐 → 校验并清理失效赠品。
 */
(() => {
  'use strict';
  if (window.KMCartBogo) return;
  const rules = window.KMCartBogoRules;
  // 页面状态：drawer 代表当前根节点， #CartDrawer || #CartPage。
  let drawer;
  let isPage = false;
  // 异步读取版本号：只接受最后一次上下文请求，避免快速切换商品时使用旧响应。
  let readRevision = 0;
  let configText = '';
  /**
   * 读取活动配置，兼容 /cart 礼品区块尚未挂载到页面根节点内的情况。
   * @param {HTMLElement} root 当前购物车根节点。
   * @returns {string} JSON 配置原文；找不到配置时返回 '[]'。
   */
  const readConfig = root => (root.querySelector('[data-km-bogo-config]') || (root.id === 'CartPage' && document.querySelector('[data-km-cart-page-gifts] [data-km-bogo-config]')))?.textContent || '[]';
  // readConfig 功能结束。

  /**
   * 构造保留语言路径的购物车接口地址。
   * 入参：无，读取当前根节点、页面表单及 Shopify 路由。
   * @returns {string} 购物车 JSON 接口地址。
   */
  const cartUrl = () => drawer.dataset.cartUrl || ((document.getElementById('CartPageForm')?.dataset.cartRoot || window.Shopify?.routes?.root || '/') + 'cart.js');
  // cartUrl 功能结束。

  // 业务状态：活动列表、购物车快照、当前来源商品和匹配活动。
  let activities = [];
  let cart = {items: []};
  let selected = null;
  let activity = null;
  // 抽屉 PC 端当前 Tab，以及用于隔离不同来源商品分页的上下文标识。
  let tab = 'pair';
  let context = '';
  // 清理函数占位：入参无，返回 undefined；初始化后替换为当前实例的事件解绑函数。
  let dispose = () => {};
  // 清理锁避免并发修改购物车；BOGO 页码与 Pair with 自身的页码分别维护。
  let reconciling = false;
  const pages = new Map();
  // 内联区域按“购物车行 key + 活动 ID”记忆 Tab；/cart 的 PC 端也复用此区域。
  const mobileTabs = new Map();
  let mobileLine = null;
  const media = window.matchMedia('(min-width: 769px)');
  // 当前语言和货币在页面生命周期内固定，复用格式器，避免每张卡重复创建。
  const currencyFormat = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency', currency: window.Shopify?.currency?.active || 'USD'
  });
  /**
   * 读取已渲染购物车行中的商品集合信息，供活动规则判断资格。
   * 入参：无，读取当前根节点内的 data 属性。
   * @returns {Object<string, string[]>} 商品 ID 到集合 ID 数组的映射。
   */
  const memberships = () => Object.fromEntries([...drawer.querySelectorAll('[data-km-cart-line]')]
    .map(line => [line.dataset.kmCartProductId, (line.dataset.kmCartCollections || '').split(',').filter(Boolean)]));
  // memberships 功能结束。

  /**
   * 创建 DOM 元素，使用 textContent 写入文字。
   * @param {string} tag HTML 标签名。
   * @param {string} [className] 元素类名；为空时不设置。
   * @param {string} [text] 元素文字；未传入时不设置。
   * @returns {HTMLElement} 尚未插入页面的 DOM 元素。
   */
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  // node 功能结束。

  /**
   * 按页面语言与店铺当前货币格式化金额。
   * @param {number|string} cents 以分为单位的金额。
   * @returns {string} 格式化后的金额，例如 $19.00。
   */
  const money = cents => currencyFormat.format(Number(cents || 0) / 100);

  // money 功能结束。

  /**
   * 清理来源商品已删除、资格失效或超过可领取数量的赠品。
   * 入参：无，使用当前活动、购物车快照及 DOM 集合信息。
   * @returns {Promise<void>} 完成校验、必要的购物车更新与重绘；错误在内部提示。
   */
  async function reconcile() {
    if (reconciling || !drawer?.isConnected || !cart.items.some(item => item.properties?._km_bogo_activity)) return;
    // 关键节点：抽屉初始商品列表为空，购物车 JSON 可能先于商品 DOM 返回。
    // 购买商品集合信息全部就绪后才允许清理，避免将“尚未加载”误判为“不符合活动”。
    const lines = [...drawer.querySelectorAll('[data-km-cart-line]')];
    const purchases = cart.items.filter(item => !rules.isGift(item));
    if (purchases.some(item => !lines.some(line => line.dataset.key === item.key
      && line.hasAttribute('data-km-cart-collections')))) return;
    const nativeCart = document.getElementById(isPage ? 'CartPageForm' : 'CartDrawerForm')?.kmCartForm;
    if (!nativeCart?.beginOperation || !nativeCart.buildCart) return;
    // 按活动和来源商品计算待修改的行 key 与数量；无需修改时直接结束。
    const changes = rules.updates(activities, cart, memberships());
    if (!Object.keys(changes).length) return;
    reconciling = true;
    // 接入原生购物车操作状态；更新后重建列表，最终释放操作状态和清理锁。
    const operation = nativeCart.beginOperation('bogo-cleanup');
    let success = false;
    try {
      const url = cartUrl().replace(/cart\.js$/, 'cart/update.js');
      const response = await (window.KMCartState?.fetch || window.fetch.bind(window))(url, {
        method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({updates: changes})
      });
      if (!response.ok) throw new Error('Unable to update free gifts.');
      await nativeCart.buildCart();
      success = true;
    } catch (error) {
      const notice = drawer.querySelector('[data-km-bogo-message]');
      if (notice) { notice.hidden = false; notice.textContent = 'Unable to update free gifts. Please refresh your cart.'; }
    } finally {
      nativeCart.finishOperation(operation, success);
      reconciling = false;
    }
  }

  // reconcile 功能结束。

  /**
   * 创建赠品卡，显示免费价、原价和当前来源商品的领取状态。
   * @param {Object} product 活动商品配置，包含 handle、title、image、price、variants 等字段。
   * @returns {HTMLElement} article 卡片节点；依赖当前 activity、cart 和 selected 状态。
   */
  function card(product) {
    const element = node('article', 'km-cart-pair__product');
    element.dataset.kmBogoProduct = product.handle;
    const image = node('div', 'km-cart-pair__image');
    if (product.image) {
      const img = node('img');
      img.src = product.image;
      img.alt = product.title;
      img.width = img.height = 240;
      img.loading = 'lazy';
      image.append(img);
    }
    const badge = node('span', 'km-cart-bogo__badge', 'FREE');
    const title = node('div', 'km-cart-pair__title', product.title);
    const footer = node('div', 'km-cart-pair__footer');
    const prices = node('div', 'km-cart-pair__prices');
    prices.append(node('strong', '', money(0)), node('s', '', money(product.price)));
    // 领取状态按“活动 + 来源商品”隔离，不同来源可以分别领取相同赠品。
    const quantity = rules.claimed(activity, cart, selected)
      .filter(item => product.variants.some(variant => String(variant.id) === String(item.variant_id || item.id)))
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    element.classList.toggle('km-cart-bogo__selected', quantity > 0);
    const button = node('button', 'km-cart-pair__open');
    const count = node('span', 'km-cart-pair__quantity', String(quantity));
    count.hidden = !quantity;
    const plus = node('span', 'km-cart-pair__plus', '+');
    plus.setAttribute('aria-hidden', 'true');
    button.classList.toggle('has-quantity', quantity > 0);
    button.append(count, plus);
    button.type = 'button';
    // 卡片打开共用规格弹窗；传递活动与来源标识，供加购复核及写入赠品属性。
    button.dataset.kmPairOpen = product.handle;
    button.dataset.kmBogoActivity = activity.id;
    button.dataset.kmBogoAnchor = rules.anchorId(selected);
    button.setAttribute('aria-label', 'Choose free gift: ' + product.title);
    button.setAttribute('aria-haspopup', 'dialog');
    button.setAttribute('aria-controls', isPage ? 'km-cart-page-pair-dialog' : 'km-cart-pair-dialog');
    footer.append(prices, button);
    element.append(image, badge, title, footer);
    return element;
  }

  // card 功能结束。

  /**
   * 渲染入口：同步所有购买商品按钮文案，再按页面和屏幕尺寸渲染推荐。
   * 入参：无，读取控制器当前状态。
   * @returns {void} 更新 DOM；活动匹配与实际领取额度分别由规则判断。
   */
  function render() {
    if (!drawer || !rules) return;
    // 包括尚未展开的商品：匹配活动显示 BOGO，否则显示 Pair with。
    const collections = memberships();
    const itemsByKey = new Map(cart.items.map(item => [item.key, item]));
    drawer.querySelectorAll('[data-km-cart-line]').forEach(line => {
      const toggle = line.querySelector('[data-km-cart-pair-toggle]');
      const label = toggle?.querySelector('span');
      if (!label) return;
      const item = itemsByKey.get(line.dataset.key);
      const text = rules.match(activities, item, collections) ? 'BOGO' : 'Pair with';
      if (label.textContent !== text) label.textContent = text;
    });
    // /cart 所有尺寸及抽屉移动端使用内联区域；抽屉 PC 使用独立推荐栏。
    if (isPage || !media.matches) { renderMobile(collections); return; }
    const root = drawer.querySelector('[data-km-cart-pair]');
    if (!root) return;
    activity = media.matches ? rules.match(activities, selected, collections) : null;
    // 来源商品、规格或活动改变时重新决定默认 Tab；无活动时回到 Pair with。
    const nextContext = `${selected?.product_id || ''}:${selected?.variant_id || selected?.id || ''}:${activity?.id || ''}`;
    if (nextContext !== context) {
      context = nextContext;
      tab = activity ? 'bogo' : 'pair';
    }
    if (!activity) tab = 'pair';
    const bogoTab = root.querySelector('[data-km-suggestion-tab="bogo"]');
    bogoTab.hidden = !activity;
    root.querySelectorAll('[data-km-suggestion-tab]').forEach(button => {
      const active = button.dataset.kmSuggestionTab === tab;
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
    });
    root.querySelector('[data-km-bogo-panel]').hidden = tab !== 'bogo';
    root.querySelector('[data-km-pair-tab-panel]').hidden = tab !== 'pair';
    if (!activity) return;
    const products = activity.products.filter(product => rules.variants(activity, product).length);
    // 抽屉 PC 每页 3 张卡；页码从 0 开始，赠品池缩小时限制到有效范围。
    const count = Math.ceil(products.length / 3);
    const page = Math.min(pages.get(context) || 0, Math.max(0, count - 1));
    pages.set(context, page);
    const list = root.querySelector('[data-km-bogo-list]');
    const visible = products.slice(page * 3, page * 3 + 3);
    const signature = JSON.stringify([context, visible, rules.claimed(activity, cart, selected)]);
    if (list.dataset.signature !== signature) {
      list.dataset.signature = signature;
      list.replaceChildren(...visible.map(card));
    }
    root.querySelector('[data-km-bogo-pagination]').hidden = count <= 1;
    root.querySelector('[data-km-bogo-prev]').disabled = page === 0;
    root.querySelector('[data-km-bogo-next]').disabled = page >= count - 1;
    root.querySelector('[data-km-bogo-page-status]').textContent = count ? `Page ${page + 1} of ${count}` : '';
    const message = root.querySelector('[data-km-bogo-message]');
    // 空赠品池和未达到购买门槛分别提示；已领取数量不影响商品按钮的活动匹配文案。
    const allowance = rules.allowance(activity, cart, collections, selected);
    message.hidden = products.length > 0 && allowance > 0;
    message.textContent = !products.length ? 'No free gifts are available right now.' : `Buy ${activity.buyQuantity} qualifying items to choose your free gifts.`;
  }

  // render 功能结束。

  /**
   * 渲染商品行内推荐，供抽屉移动端和 /cart 所有屏幕尺寸复用。
   * @param {Object<string, string[]>} [collections] 商品所属集合映射；省略时读取当前 DOM。
   * 优先使用 mobileLine，否则查找当前展开的购物车行。
   * @returns {void} 创建或更新 Tab、赠品列表和 /cart PC 分页控件。
   */
  function renderMobile(collections = memberships()) {
    const line = mobileLine?.isConnected ? mobileLine : drawer.querySelector('[data-km-cart-pair-inline].is-open')?.closest('[data-km-cart-line]');
    if (!line) return;
    selected = cart.items.find(item => item.key === line.dataset.key) || null;
    activity = rules.match(activities, selected, collections);
    const viewport = line.querySelector('.km-cart-pair-inline__viewport');
    if (!viewport) return;
    let tabs = viewport.querySelector('[data-km-mobile-bogo-tabs]');
    // 每个行内区域只创建一次 Tab 和赠品面板，避免切换时重复插入节点。
    if (!tabs) {
      tabs = node('div', 'km-cart-pair__tabs');
      tabs.dataset.kmMobileBogoTabs = '';
      tabs.setAttribute('role', 'tablist');
      tabs.setAttribute('aria-label', 'Product suggestions');
      for (const [name, label] of [['bogo', 'BOGO'], ['pair', 'Pair with']]) {
        const button = node('button', 'km-cart-pair__tab', label);
        button.type = 'button';
        button.dataset.kmMobileSuggestionTab = name;
        button.setAttribute('role', 'tab');
        button.id = `km-mobile-${line.dataset.key.replace(/[^a-z0-9]/gi, '-')}-${name}-tab`;
        tabs.append(button);
      }
      viewport.prepend(tabs);
      const panel = node('div', 'km-cart-bogo__mobile');
      panel.dataset.kmMobileBogoPanel = '';
      panel.setAttribute('role', 'tabpanel');
      panel.id = tabs.firstElementChild.id + '-panel';
      panel.setAttribute('aria-labelledby', tabs.firstElementChild.id);
      tabs.firstElementChild.setAttribute('aria-controls', panel.id);
      const list = node('div', 'km-cart-bogo__list');
      list.dataset.kmMobileBogoList = '';
      panel.append(list);
      viewport.append(panel);
      // /cart 额外创建独立 BOGO 箭头，选择器与 Pair with 箭头区分。
      if (isPage) {
        const controls = node('div', 'km-cart-pair__pagination');
        controls.dataset.kmInlineBogoPagination = '';
        for (const [direction, symbol] of [['prev', '\u2039'], ['next', '\u203a']]) {
          const button = node('button', 'km-cart-pair__arrow', symbol);
          button.type = 'button';
          button.setAttribute(`data-km-inline-bogo-${direction}`, '');
          button.setAttribute('aria-label', `${direction === 'prev' ? 'Previous' : 'Next'} free gifts`);
          controls.append(button);
        }
        const status = node('span', 'visually-hidden');
        status.dataset.kmInlineBogoStatus = '';
        status.setAttribute('aria-live', 'polite');
        panel.append(controls, status);
      }
    }
    // 按来源行和活动记忆选择；没有匹配活动则隐藏 BOGO Tab 并显示 Pair with。
    const key = `${line.dataset.key}:${activity?.id || ''}`;
    const active = activity ? mobileTabs.get(key) || 'bogo' : 'pair';
    tabs.querySelector('[data-km-mobile-suggestion-tab="bogo"]').hidden = !activity;
    tabs.querySelectorAll('button').forEach(button => {
      button.setAttribute('aria-selected', String(button.dataset.kmMobileSuggestionTab === active));
      button.tabIndex = button.dataset.kmMobileSuggestionTab === active ? 0 : -1;
    });
    viewport.dataset.kmSuggestionActive = active;
    const panel = viewport.querySelector('[data-km-mobile-bogo-panel]');
    panel.hidden = active !== 'bogo';
    const list = panel.querySelector('[data-km-mobile-bogo-list]');
    const products = activity ? activity.products.filter(product => rules.variants(activity, product).length) : [];
    // /cart PC 每页 4 张卡；移动端渲染完整赠品池供横向滑动，不显示分页箭头。
    const paged = isPage && media.matches;
    const count = Math.ceil(products.length / 4);
    const page = paged ? Math.min(pages.get(key) || 0, Math.max(0, count - 1)) : 0;
    if (isPage) {
      const controls = panel.querySelector('[data-km-inline-bogo-pagination]');
      controls.hidden = !paged || count <= 1;
      controls.querySelector('[data-km-inline-bogo-prev]').disabled = page === 0;
      controls.querySelector('[data-km-inline-bogo-next]').disabled = page >= count - 1;
      panel.querySelector('[data-km-inline-bogo-status]').textContent = paged && count ? `Page ${page + 1} of ${count}` : '';
    }
    const visible = paged ? products.slice(page * 4, page * 4 + 4) : products;
    // 活动、可见商品或领取状态改变时才替换卡片，并保留横向滚动位置。
    const signature = JSON.stringify([activity?.id, visible, rules.claimed(activity || {}, cart, selected)]);
    if (list.dataset.signature !== signature) {
      list.dataset.signature = signature;
      const scroll = list.scrollLeft;
      list.replaceChildren(...visible.map(card));
      if (!products.length && activity) list.append(node('p', 'km-cart-pair__message', 'No free gifts are available right now.'));
      list.scrollLeft = scroll;
    }
  }

  // renderMobile 功能结束。

  /**
   * 初始化控制器；根节点或配置变化时解绑旧事件并重新绑定。
   * 入参：无，优先使用 /cart 根节点，否则使用抽屉根节点。
   * @returns {void} 完成配置解析、事件绑定及首次渲染；相同根节点及配置直接返回。
   */
  function init() {
    const next = document.getElementById('CartPage') || document.getElementById('CartDrawer');
    if (!next || (next === drawer && readConfig(next) === configText)) return;
    // 释放旧事件绑定并使旧异步读取失效，再切换当前购物车根节点。
    dispose();
    drawer = next;
    isPage = drawer.id === 'CartPage';
    mobileLine = null;
    readRevision++;
    context = '';
    pages.clear();
    configText = readConfig(drawer);
    try { activities = JSON.parse(configText); }
    catch (error) { activities = []; }
    /**
     * 接收抽屉当前商品与购物车快照，更新展示并清理失效赠品。
     * @param {CustomEvent} event detail.cart 为购物车快照，detail.item 为当前商品或 null。
     * @returns {void} 仅处理当前根节点内部派发的事件。
     */
    const onSelection = event => {
      if (!drawer.contains(event.target)) return;
      cart = event.detail.cart;
      selected = event.detail.item;
      render();
      reconcile();
    };
    // onSelection 功能结束。

    /**
     * 通过事件委托处理内联 Tab、/cart BOGO 箭头及抽屉 PC 的 Tab 和箭头。
     * @param {MouseEvent} event 当前根节点的点击事件。
     * @returns {void} 更新对应状态并重绘；各区域使用独立选择器与页码上下文。
     */
    const onClick = event => {
      const mobileTab = event.target.closest('[data-km-mobile-suggestion-tab]');
      if (mobileTab && drawer.contains(mobileTab) && (isPage || !media.matches)) {
        event.preventDefault();
        mobileLine = mobileTab.closest('[data-km-cart-line]');
        const item = cart.items.find(item => item.key === mobileLine.dataset.key);
        const offer = rules.match(activities, item, memberships());
        mobileTabs.set(`${mobileLine.dataset.key}:${offer?.id || ''}`, mobileTab.dataset.kmMobileSuggestionTab);
        renderMobile();
        return;
      }
      // /cart 的 BOGO 分页单独处理，不触发 Pair with 翻页逻辑。
      const inlineArrow = event.target.closest('[data-km-inline-bogo-prev], [data-km-inline-bogo-next]');
      if (isPage && inlineArrow && !inlineArrow.disabled && media.matches) {
        event.preventDefault();
        mobileLine = inlineArrow.closest('[data-km-cart-line]');
        const item = cart.items.find(item => item.key === mobileLine.dataset.key);
        const offer = rules.match(activities, item, memberships());
        const key = `${mobileLine.dataset.key}:${offer?.id || ''}`;
        pages.set(key, Math.max(0, (pages.get(key) || 0) + (inlineArrow.hasAttribute('data-km-inline-bogo-next') ? 1 : -1)));
        renderMobile();
        return;
      }
      const button = event.target.closest('[data-km-suggestion-tab], [data-km-bogo-prev], [data-km-bogo-next]');
      if (!button || !drawer.contains(button) || !media.matches) return;
      event.preventDefault();
      if (button.dataset.kmSuggestionTab) tab = button.dataset.kmSuggestionTab;
      else if (!button.disabled) pages.set(context, (pages.get(context) || 0) + (button.hasAttribute('data-km-bogo-next') ? 1 : -1));
      render();
    };
    // onClick 功能结束。

    /**
     * 支持方向键、Home、End 切换可见 Tab 并移动焦点。
     * @param {KeyboardEvent} event Tab 按钮触发的键盘事件。
     * @returns {void} 调用按钮 click 复用切换逻辑；其他按键不处理。
     */
    const onKey = event => {
      if (event.target.matches('[data-km-mobile-suggestion-tab]') && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        const buttons = [...event.target.parentElement.querySelectorAll('button')].filter(button => !button.hidden);
        const index = buttons.indexOf(event.target);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
          : (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
        event.preventDefault();
        buttons[next].click();
        buttons[next].focus();
        return;
      }
      if (!event.target.matches('[data-km-suggestion-tab]') || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const buttons = [...drawer.querySelectorAll('[data-km-suggestion-tab]')].filter(button => !button.hidden);
      const index = buttons.indexOf(event.target);
      const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
        : (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
      event.preventDefault();
      buttons[nextIndex].click();
      buttons[nextIndex].focus();
    };
    // onKey 功能结束。

    // 开始绑定当前实例的上下文、点击、键盘与屏幕断点事件。
    drawer.addEventListener('km:cart-selection', onSelection);
    /**
     * 切换抽屉移动端的来源商品行。
     * @param {CustomEvent} event detail.line 为来源商品的 DOM 行节点。
     * @returns {void} 更新 mobileLine 并触发渲染。
     */
    const onMobileContext = event => { mobileLine = event.detail.line; render(); };
    // onMobileContext 功能结束。

    /**
     * 接收 /cart 展开的商品行，读取最新购物车以验证当前状态。
     * @param {CustomEvent|Object} event detail.line 为当前行；无展开商品时可为 null 或 undefined。
     * @returns {Promise<void>} 最新请求成功后更新快照、重绘并尝试清理；读取失败内部处理。
     */
    const onPageContext = async event => {
      mobileLine = event.detail.line;
      render();
      // 每次切换增加版本号，旧响应即使后返回也不会覆盖当前上下文。
      const revision = ++readRevision;
      try {
        const snapshot = window.KMCartState?.read
          // 展开/切换只改变展示；购物车写入、重新打开和跨标签事件负责使缓存失效。
          // 真正领取赠品时，弹窗仍使用 fresh:true 再次校验。
          ? await window.KMCartState.read(cartUrl(), {fresh: false})
          : await fetch(cartUrl(), {credentials: 'same-origin'}).then(response => {
            if (!response.ok) throw new Error('Unable to load cart.');
            return response.json();
          });
        if (revision !== readRevision || !drawer?.isConnected) return;
        cart = snapshot;
        render();
        reconcile();
      } catch (error) {
        // 读取失败时保留普通推荐交互；赠品真正加购前，弹窗还会校验最新购物车。
      }
    };
    // onPageContext 功能结束。
    drawer.addEventListener('km:cart-page-pair-context', onPageContext);
    drawer.addEventListener('km:cart-mobile-pair-context', onMobileContext);
    drawer.addEventListener('click', onClick);
    drawer.addEventListener('keydown', onKey);
    media.addEventListener('change', render);
    /**
     * 释放当前实例绑定的事件，并使尚未完成的 /cart 读取失效。
     * 入参：无。
     * @returns {void} 使用本次初始化闭包中的处理函数解绑监听。
     */
    dispose = () => {
      drawer.removeEventListener('km:cart-selection', onSelection);
      drawer.removeEventListener('km:cart-mobile-pair-context', onMobileContext);
      readRevision++;
      drawer.removeEventListener('km:cart-page-pair-context', onPageContext);
      drawer.removeEventListener('click', onClick);
      drawer.removeEventListener('keydown', onKey);
      media.removeEventListener('change', render);
    };
    // dispose 功能结束。

    // 首次展示：抽屉沿用商品面板快照；/cart 主动读取最新购物车。
    const panel = drawer.querySelector('km-cart-product-panel');
    cart = panel?.cart || {items: []};
    selected = cart.items.find(item => item.key === panel?.selection?.key) || null;
    render();
    if (isPage) onPageContext({detail: {line: drawer.querySelector('[data-km-cart-pair-inline].is-open')?.closest('[data-km-cart-line]')}});
    else reconcile();
  }

  // init 功能结束。

  // 提供给共用赠品弹窗的公开接口；init 的入参和返回值见上述函数说明。
  window.KMCartBogo = {
    init,
    /**
     * 根据活动 ID 查询当前启用的活动。
     * @param {string} id 后台活动区块 ID。
     * @returns {Object|undefined} 匹配活动配置；不存在时返回 undefined。
     */
    getActivity: id => activities.find(value => value.id === id),
    // getActivity 功能结束。

    /**
     * 校验来源商品能否领取某个赠品规格；来源必须仍存在且匹配活动。
     * @param {string} id 活动 ID。
     * @param {string|number} variantId 待领取赠品的规格 ID。
     * @param {Object} [snapshot=cart] 购物车快照，弹窗提交时可传最新快照。
     * @param {string} [source] 来源标识（商品 ID:规格 ID）；未传时使用 selected 的行 key。
     * @returns {boolean} 是否满足赠品池、SKU、额度和重复领取规则。
     */
    canAdd: (id, variantId, snapshot = cart, source) => {
      const offer = activities.find(value => value.id === id);
      const anchor = snapshot.items.find(item => !rules.isGift(item) && (source ? rules.anchorId(item) === source : item.key === selected?.key));
      return Boolean(offer && rules.match([offer], anchor, memberships())
        && rules.canAdd(offer, snapshot, memberships(), variantId, anchor));
    }
  };
  // canAdd 及公开接口定义结束。

  // 主题编辑器重载区块时重新初始化；卸载当前购物车区块时释放事件。
  document.addEventListener('shopify:section:load', init);
  /**
   * 处理区块卸载，仅清理包含当前购物车根节点的区块。
   * @param {CustomEvent} event target 为卸载的区块节点。
   * @returns {void} 释放事件监听并清空根节点引用。
   */
  document.addEventListener('shopify:section:unload', event => {
    if (drawer && event.target.contains(drawer)) { dispose(); drawer = null; }
  });
  // 区块卸载处理功能结束。

  // 脚本加载后执行首次初始化。
  init();
})();
